'use client';

import { c as _c } from "react/compiler-runtime";
import { usePrefetchGetUser } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { userIdentityKey } from '@/components/profile/user-identity-key';
import { DataTable } from '@/components/ui/data-table';
import { getProfileHref } from '@/lib/profile/profile-href';
import { PORTFOLIO_USER_PROFILE_FIELDS } from '@/lib/sdk/projections';
import { MarketBoosterRowCells } from './market-booster-row-cells';
import type { MarketBoosterLeaderboardEntry, MarketBoosterSortValue } from './types';

// ---------------------------------------------------------------------------
// Re-export the entry type under a shorter alias for callers that still
// reference `MarketBoosterEntry` (the legacy name).
// ---------------------------------------------------------------------------
export type MarketBoosterEntry = MarketBoosterLeaderboardEntry;
interface MarketBoostersTableProps {
  entries: MarketBoosterEntry[];
  currentWalletAddress: string | null;
  sortBy: MarketBoosterSortValue;
  onSortChange: (sort: MarketBoosterSortValue) => void;
}

// The backend hardcodes `sortOrder: 'desc'` (see queries/market-boosters-leaderboard.ts).
// We expose order toggling visually but the backend ignores it; an explicit no-op
// keeps the DataTable's controlled-sort contract satisfied without misleading users.
function noopOrderChange(): void {
  // Intentional no-op until the backend supports asc-order for this endpoint.
}
export function MarketBoostersTable(t0) {
  const $ = _c(32);
  const {
    entries,
    currentWalletAddress,
    sortBy,
    onSortChange
  } = t0;
  const t = useTranslations("market.boosters");
  const prefetchUser = usePrefetchGetUser();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DataTable.RankColumn />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== t) {
    t2 = t("column_booster");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <DataTable.IdentityColumn header={t2} className="w-auto max-w-none" />;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("column_date");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <DataTable.SortableColumn sortKey="lastBoost" header={t4} align="right" className="w-28 shrink-0" />;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t) {
    t6 = t("column_amount");
    $[9] = t;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <DataTable.SortableColumn sortKey="amount" header={t6} align="right" className="w-28 shrink-0" />;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t) {
    t8 = t("column_number_of_boosts");
    $[13] = t;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t8) {
    t9 = <DataTable.SortableColumn sortKey="boosts" header={t8} align="right" className="w-20 shrink-0" />;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t3 || $[18] !== t5 || $[19] !== t7 || $[20] !== t9) {
    t10 = <DataTable.Header>{t1}{t3}{t5}{t7}{t9}</DataTable.Header>;
    $[17] = t3;
    $[18] = t5;
    $[19] = t7;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== currentWalletAddress || $[23] !== prefetchUser || $[24] !== t) {
    t11 = <DataTable.Body>{entry => {
        const rowKey = entry.participationId.length > 0 ? entry.participationId : userIdentityKey(entry.booster, `anonymous-rank-${entry.rank}`);
        const isUser = currentWalletAddress !== null && entry.booster.walletAddress === currentWalletAddress;
        const hasProfile = entry.booster.userId !== null;
        const href = hasProfile && entry.booster.userId !== null ? getProfileHref(entry.booster.userId) : undefined;
        const prefetch = hasProfile && entry.booster.userId !== null ? () => {
          if (entry.booster.userId === null) {
            return;
          }
          prefetchUser({
            identifier: entry.booster.userId,
            fields: [...PORTFOLIO_USER_PROFILE_FIELDS]
          });
        } : undefined;
        return <DataTable.Row key={rowKey} isCurrentUser={isUser} ariaLabel={t("row_aria_label", {
          name: entry.booster.displayName ?? entry.booster.handle ?? "",
          rank: entry.rank,
          amount: entry.totalAmount,
          count: entry.numBoosts
        })} {...href !== undefined ? {
          href
        } : {}} {...prefetch !== undefined ? {
          onMouseEnter: prefetch,
          onFocus: prefetch,
          onPointerDown: prefetch
        } : {}} data-testid="market-boosters-row"><MarketBoosterRowCells entry={entry} isUser={isUser} /></DataTable.Row>;
      }}</DataTable.Body>;
    $[22] = currentWalletAddress;
    $[23] = prefetchUser;
    $[24] = t;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== entries || $[27] !== onSortChange || $[28] !== sortBy || $[29] !== t10 || $[30] !== t11) {
    t12 = <DataTable entries={entries} sortBy={sortBy} sortOrder="desc" onSortChange={onSortChange} onSortOrderChange={noopOrderChange} panel="borderless">{t10}{t11}</DataTable>;
    $[26] = entries;
    $[27] = onSortChange;
    $[28] = sortBy;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  return t12;
}